// Phase 16A: Auto Bank Reconciliation - statement import + auto-match, on
// top of the existing (Phase 16) manual reconciliation page. Rendered
// inside BankReconciliationManualPage.jsx for the currently selected Bank
// Account. Confirming a match here calls the exact same backend function
// Phase 16's own manual Reconcile button uses - see server.py's "PHASE 16A"
// section - so there is one single reconciliation record per voucher line,
// never two parallel systems.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../../components/ui/dialog";
import { toast } from "sonner";
import { SectionCard, EmptyState } from "./shared";
import { ACCOUNTING_BASE } from "./accountingNav";
import { formatCurrency, formatDateDMY } from "./LineGrid";

const STATUS_LABELS = { auto_matched: "Auto Matched", suggested: "Suggested Match", unmatched: "Unmatched", already_reconciled: "Already Reconciled" };
const STATUS_TONE = {
  auto_matched: "bg-emerald-50 text-emerald-700", suggested: "bg-amber-50 text-amber-700",
  unmatched: "bg-[#F3F4F6] text-[#6B7280]", already_reconciled: "bg-[#EEF2FF] text-[#4338CA]",
};

function StatusBadge({ status }) {
  return <span className={`rounded-sm px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONE[status] || "bg-[#F3F4F6] text-[#6B7280]"}`}>{STATUS_LABELS[status] || status}</span>;
}

async function fileToBase64(file) {
  const buf = await file.arrayBuffer();
  return btoa(new Uint8Array(buf).reduce((s, b) => s + String.fromCharCode(b), ""));
}

/** "Find Voucher" / "Change Match" search dialog for one staged row. */
function FindVoucherDialog({ row, busy, onCancel, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const search = useCallback(async () => {
    if (!row) return;
    try {
      setSearching(true);
      const res = await api.get(`/society/accounting/bank-reconcile/import-rows/${row.id}/candidates`, { params: { query: q } });
      setResults(res.data?.results || []);
    } catch (err) {
      toast.error(formatErr(err, "Search failed"));
    } finally {
      setSearching(false);
    }
  }, [row, q]);
  useEffect(() => { if (row) search(); }, [row]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Find Voucher</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by voucher no., party, narration, reference or amount" onKeyDown={(e) => e.key === "Enter" && search()} />
            <Button type="button" variant="outline" disabled={searching} onClick={search}>Search</Button>
          </div>
          <div className="max-h-80 overflow-y-auto rounded-sm border border-[#E5E7EB]">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Voucher</TableHead><TableHead>Party</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {results.map((r) => (
                  <TableRow key={`${r.voucherId}-${r.voucherLineId}`}>
                    <TableCell>{formatDateDMY(r.voucherDate)}</TableCell>
                    <TableCell>{r.voucherNumber}</TableCell>
                    <TableCell className="max-w-[160px] truncate">{r.partyName || r.narration || "-"}</TableCell>
                    <TableCell className="text-right">{r.debit ? formatCurrency(r.debit) : "—"}</TableCell>
                    <TableCell className="text-right">{r.credit ? formatCurrency(r.credit) : "—"}</TableCell>
                    <TableCell><Button size="sm" disabled={busy} onClick={() => onPick(r)}>Use this</Button></TableCell>
                  </TableRow>
                ))}
                {results.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#6B7280]">No matching vouchers.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
        <DialogFooter><Button type="button" variant="outline" onClick={onCancel}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BankReconcileImportPanel({ bankAccountId, onReconciled }) {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { format, preMapped, headers, sampleRows, totalRows, canonicalFields, warnings }
  const [columnMap, setColumnMap] = useState({});
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [findVoucherRow, setFindVoucherRow] = useState(null);

  const loadRows = useCallback(async () => {
    if (!bankAccountId) { setRows([]); return; }
    try {
      setLoadingRows(true);
      const res = await api.get("/society/accounting/bank-reconcile/import-rows", { params: { bankAccountId } });
      setRows(res.data?.rows || []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load imported statement rows"));
    } finally {
      setLoadingRows(false);
    }
  }, [bankAccountId]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const onFileChosen = async (f) => {
    setFile(f);
    setPreview(null);
    setColumnMap({});
    try {
      setBusy(true);
      const isText = /\.csv$/i.test(f.name);
      const body = isText
        ? { bankAccountId, fileName: f.name, csvText: await f.text() }
        : { bankAccountId, fileName: f.name, fileBase64: await fileToBase64(f) };
      const res = await api.post("/society/accounting/bank-reconcile/import/preview", body);
      setPreview({ ...res.data, ...body });
    } catch (err) {
      toast.error(formatErr(err, "Could not read this file"));
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const canImport = preview && (preview.preMapped || (columnMap.date && (columnMap.debit || columnMap.credit || columnMap.amount)));

  const runImport = async () => {
    if (!canImport) return;
    try {
      setBusy(true);
      const res = await api.post("/society/accounting/bank-reconcile/import", {
        bankAccountId, fileName: preview.fileName, csvText: preview.csvText || "", fileBase64: preview.fileBase64 || "", columnMap,
      });
      const r = res.data || {};
      toast.success(`Imported ${r.newRowCount} new row(s)${r.duplicateRowCount ? `, ${r.duplicateRowCount} duplicate row(s) skipped` : ""}`);
      setFile(null); setPreview(null); setColumnMap({});
      await loadRows();
    } catch (err) {
      toast.error(formatErr(err, "Import failed"));
    } finally {
      setBusy(false);
    }
  };

  const accept = async (row, voucherId = "", voucherLineId = "") => {
    try {
      setBusy(true);
      await api.post(`/society/accounting/bank-reconcile/import-rows/${row.id}/accept`, { voucherId, voucherLineId });
      toast.success("Match accepted - click Confirm Auto Reconciliation below to finalize");
      setFindVoucherRow(null);
      await loadRows();
    } catch (err) {
      toast.error(formatErr(err, "Could not accept this match"));
    } finally {
      setBusy(false);
    }
  };

  const removeMatch = async (row) => {
    try {
      setBusy(true);
      await api.post(`/society/accounting/bank-reconcile/import-rows/${row.id}/remove-match`, {});
      await loadRows();
    } catch (err) {
      toast.error(formatErr(err, "Could not remove this match"));
    } finally {
      setBusy(false);
    }
  };

  const acceptedPending = rows.filter((r) => r.accepted && !r.confirmed);
  const confirmAll = async () => {
    try {
      setBusy(true);
      const res = await api.post("/society/accounting/bank-reconcile/import/confirm", { bankAccountId });
      const r = res.data || {};
      if (r.failed) toast.error(`${r.failed} of ${r.confirmed + r.failed} could not be confirmed`);
      toast.success(`${r.confirmed} transaction(s) reconciled`);
      await loadRows();
      onReconciled?.();
    } catch (err) {
      toast.error(formatErr(err, "Could not confirm auto reconciliation"));
    } finally {
      setBusy(false);
    }
  };

  const createEntry = (row, voucherType) => {
    const params = new URLSearchParams({
      prefillAmount: String(row.debit || row.credit || row.rawAmount || ""),
      prefillDate: row.transactionDate || "", prefillNarration: row.narration || "",
      prefillReferenceNo: row.referenceNo || row.utr || row.chequeNo || "", prefillBankAccountId: bankAccountId,
    });
    if (voucherType === "CONTRA") params.set("prefillDirection", row.debit > 0 ? "in" : "out");
    const path = voucherType === "RECEIPT" ? "receipts" : voucherType === "PAYMENT" ? "payments" : "contra";
    navigate(`${ACCOUNTING_BASE}/${path}?${params.toString()}`);
  };

  if (!bankAccountId) return null;

  return (
    <SectionCard
      title="Auto Bank Reconciliation - Import Bank Statement"
      subtitle="Import a bank statement (Excel, CSV or PDF) and match it against your posted Receipt, Payment and Contra entries. Importing never changes the General Ledger, Bank Book or any voucher amount - only Confirm below does, and only for matches you've accepted."
      actions={acceptedPending.length > 0 && <Button disabled={busy} onClick={confirmAll}>Confirm Auto Reconciliation ({acceptedPending.length})</Button>}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Input type="file" accept=".csv,.xlsx,.xlsm,.pdf" onChange={(e) => e.target.files[0] && onFileChosen(e.target.files[0])} className="max-w-sm" />
          {file && <span className="text-xs text-[#6B7280]">{file.name}</span>}
        </div>

        {preview && preview.warnings?.length > 0 && (
          <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            {preview.warnings.slice(0, 5).map((w, i) => <div key={i}>{w}</div>)}
            {preview.warnings.length > 5 && <div>...and {preview.warnings.length - 5} more.</div>}
          </div>
        )}

        {preview && !preview.preMapped && (
          <div className="space-y-3">
            <div className="text-sm font-semibold text-[#111827]">{preview.totalRows} row(s) detected. Map the columns below:</div>
            <div className="grid gap-3 sm:grid-cols-3">
              {preview.canonicalFields.filter((f) => f !== "amount" || !preview.headers.some((h) => columnMap.debit === h || columnMap.credit === h)).map((field) => (
                <div key={field} className="space-y-1">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">{field}{field === "date" ? " *" : ""}</div>
                  <select className="h-9 w-full rounded-sm border border-[#D1D5DB] bg-white px-2 text-sm" value={columnMap[field] || ""} onChange={(e) => setColumnMap((m) => ({ ...m, [field]: e.target.value }))}>
                    <option value="">(not mapped)</option>
                    {preview.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
              <Table>
                <TableHeader><TableRow>{preview.headers.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {preview.sampleRows.map((r, i) => <TableRow key={i}>{preview.headers.map((h) => <TableCell key={h} className="text-xs">{r[h]}</TableCell>)}</TableRow>)}
                </TableBody>
              </Table>
            </div>
            <Button disabled={!canImport || busy} onClick={runImport}>Import Statement</Button>
          </div>
        )}

        {preview && preview.preMapped && (
          <div className="space-y-3">
            <div className="text-sm font-semibold text-[#111827]">{preview.totalRows} transaction(s) extracted from this PDF (best-effort text extraction - review before importing).</div>
            <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Narration</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Reliable</TableHead></TableRow></TableHeader>
                <TableBody>
                  {preview.sampleRows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{formatDateDMY(r.transactionDate)}</TableCell>
                      <TableCell className="max-w-[280px] truncate">{r.narration}</TableCell>
                      <TableCell className="text-right">{formatCurrency(r._rawAmount || 0)}</TableCell>
                      <TableCell>{r.reliable ? <span className="text-emerald-700">Yes</span> : <span className="text-amber-700">Needs review</span>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Button disabled={busy} onClick={runImport}>Import Statement</Button>
          </div>
        )}

        <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Narration</TableHead><TableHead>Ref/UTR/Cheque</TableHead>
                <TableHead className="text-right">Deposit</TableHead><TableHead className="text-right">Withdrawal</TableHead>
                <TableHead>Match Status</TableHead><TableHead>Matched Voucher</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingRows ? (
                <TableRow><TableCell colSpan={8} className="text-center text-sm text-[#6B7280]">Loading...</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} className={!r.reliable ? "bg-amber-50/40" : ""}>
                  <TableCell>{formatDateDMY(r.transactionDate)}</TableCell>
                  <TableCell className="max-w-[200px] truncate">{r.narration}{!r.reliable && <span className="ml-1 text-amber-700">(needs review)</span>}</TableCell>
                  <TableCell className="text-xs text-[#6B7280]">{r.referenceNo || r.chequeNo || r.utr || "-"}</TableCell>
                  <TableCell className="text-right">{r.debit ? formatCurrency(r.debit) : "—"}</TableCell>
                  <TableCell className="text-right">{r.credit ? formatCurrency(r.credit) : "—"}</TableCell>
                  <TableCell>
                    <StatusBadge status={r.confirmed ? "already_reconciled" : r.matchStatus} />
                    {r.matchTier && !r.confirmed && <span className="ml-1 text-[10px] text-[#6B7280]">{r.matchTier}</span>}
                  </TableCell>
                  <TableCell>
                    {r.matchedVoucherId ? (
                      <button type="button" className="text-xs font-semibold text-[#111827] hover:underline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${r.matchedVoucherId}`)}>{r.matchedVoucherNumber}</button>
                    ) : r.matchStatus === "suggested" && r.suggestedMatches?.length > 0 ? (
                      <div className="space-y-1">
                        {r.suggestedMatches.slice(0, 3).map((c, i) => (
                          <div key={i} className="text-xs text-[#6B7280]">{c.voucherNumber} ({c.tier}, {c.signals?.join(", ") || "amount only"})</div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-[#9CA3AF]">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {r.confirmed ? (
                      <span className="text-xs text-[#9CA3AF]">Reconciled</span>
                    ) : r.matchStatus === "already_reconciled" ? (
                      <button type="button" className="text-xs font-semibold text-[#111827] hover:underline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${r.matchedVoucherId}`)}>View Voucher</button>
                    ) : (
                      <div className="flex flex-wrap justify-end gap-2 text-xs font-semibold">
                        {!r.accepted && (r.matchStatus === "auto_matched" || (r.matchStatus === "suggested" && r.suggestedMatches?.length > 0)) && (
                          <button type="button" className="text-[#111827] hover:underline" disabled={busy} onClick={() => accept(r)}>Accept</button>
                        )}
                        <button type="button" className="text-[#111827] hover:underline" disabled={busy || !r.reliable} onClick={() => setFindVoucherRow(r)}>{r.accepted ? "Change Match" : "Find Voucher"}</button>
                        {r.accepted && <button type="button" className="text-red-600 hover:underline" disabled={busy} onClick={() => removeMatch(r)}>Remove Match</button>}
                        {r.matchStatus === "unmatched" && r.reliable && (
                          <>
                            {r.debit > 0 ? (
                              <>
                                <button type="button" className="text-[#111827] hover:underline" onClick={() => createEntry(r, "RECEIPT")}>+ Receipt</button>
                                <button type="button" className="text-[#111827] hover:underline" onClick={() => createEntry(r, "CONTRA")}>+ Contra</button>
                              </>
                            ) : (
                              <>
                                <button type="button" className="text-[#111827] hover:underline" onClick={() => createEntry(r, "PAYMENT")}>+ Payment</button>
                                <button type="button" className="text-[#111827] hover:underline" onClick={() => createEntry(r, "CONTRA")}>+ Contra</button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loadingRows && rows.length === 0 && <TableRow><TableCell colSpan={8}><EmptyState>No statement imported yet for this Bank Account.</EmptyState></TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>

      <FindVoucherDialog row={findVoucherRow} busy={busy} onCancel={() => setFindVoucherRow(null)} onPick={(r) => accept(findVoucherRow, r.voucherId, r.voucherLineId)} />
    </SectionCard>
  );
}
